///////////////////////////////////////////////////////////////////// 
// Copyright (c) Autodesk, Inc.
// Written by Autodesk Design Automation team for Inventor
/////////////////////////////////////////////////////////////////////

import repo from "../Repository";
import { addError, addLog } from "./notificationActions";
import { Jobs } from "../JobManager";
import {
    showModalProgress,
    showUpdateFailed,
    setErrorData,
    setStats,
    setReportUrl
} from "./uiFlagsActions";
import { updateProject } from "./projectListActions";
import { getProject } from "../reducers/mainReducer";

const actionTypes = {
    PARAMETERS_UPDATED: "PARAMETERS_UPDATED",
    PARAMETER_EDITED: "PARAMETER_EDITED",
    PARAMETERS_RESET: "PARAMETERS_RESET"
};

export default actionTypes;

// ======================================================
// REDUX ACTIONS
// ======================================================

export const updateParameters = (projectId, parameters) => ({
    type: actionTypes.PARAMETERS_UPDATED,
    projectId,
    parameters
});

export const editParameter = (projectId, parameter) => ({
    type: actionTypes.PARAMETER_EDITED,
    projectId,
    parameter
});

export const resetParametersAction = (projectId) => ({
    type: actionTypes.PARAMETERS_RESET,
    projectId
});

// ======================================================
// ADAPTACJA PARAMETRÓW Z BACKENDU
// ======================================================

const unquote = (input) => {
    if (input == null || input.length < 2) return input;
    if (input.startsWith('"') && input.endsWith('"')) {
        return input.slice(1, -1);
    }
    return input;
};

const toFriendly = (key) => (key || "").replace(/_/g, " ").trim();

export function adaptParameters(rawParameters) {
    return Object.entries(rawParameters).map(([key, param]) => {
        const labelFromServer =
            param.label && param.label.trim().length > 0
                ? param.label.trim()
                : undefined;

        let label;
        if (key === "Ławka_ilość") {
            label = "Ilość ławek";
        } else if (key === "Fotel_ilość") {
            label = "Ilość krzeseł";
        } else {
            label = labelFromServer || toFriendly(key);
        }

        return {
            name: label,
            apiKey: key,
            value: unquote(param.value),
            allowedValues: param.values
                ? param.values.map((v) => unquote(v))
                : [],
            units: param.unit,
            label: label,
            readonly: !!param.readonly,
            errormessage: param.errormessage
        };
    });
}

// ======================================================
// POBIERANIE PARAMETRÓW
// ======================================================

export const fetchParameters = (projectId) => async (dispatch, getState) => {
    if (!projectId) return;

    const existing = getState().updateParameters[projectId];
    if (existing && existing.length !== 0) return;

    dispatch(addLog("get parameters invoked"));

    try {
        const raw = await repo.loadParameters(projectId);
        const adapted = adaptParameters(raw);
        dispatch(updateParameters(projectId, adapted));
        dispatch(addLog("parameters received"));
    } catch (error) {
        dispatch(
            addError(
                "Failed to get parameters for " +
                projectId +
                ". (" +
                error +
                ")"
            )
        );
    }
};

// ======================================================
// RESET PARAMETRÓW DO WARTOŚCI BAZOWYCH + PRZELICZENIE MODELU
// ======================================================

export const resetParameters = (projectId) => async (dispatch) => {
    if (!projectId) return;

    dispatch(addLog("resetParameters invoked"));

    try {
        const raw = await repo.loadParameters(projectId);
        const adapted = adaptParameters(raw);

        dispatch(updateParameters(projectId, adapted));
        await dispatch(updateModelWithParameters(projectId, adapted));
    } catch (error) {
        dispatch(
            addError(
                "Failed to reset parameters for " +
                projectId +
                ". (" +
                error +
                ")"
            )
        );
    }
};

// ======================================================
// FORMAT DANYCH → BACKEND
// ======================================================

export function formatParameters(clientParameters) {
    const wrap = (val) => `"${val}"`;

    return clientParameters.reduce((acc, p) => {
        const key = p.apiKey || p.name;

        const useQuotes = p.allowedValues && p.allowedValues.length > 0;
        const val = useQuotes ? wrap(p.value) : p.value;

        acc[key] = { value: val };
        return acc;
    }, {});
}

// ======================================================
// POMOCNICZE FUNKCJE DO WALIDACJI MEBLI
// ======================================================

const numFromString = (v) => {
    if (v == null) return NaN;
    const m = v.toString().trim().match(/^(-?\d+(\.\d+)?)/);
    return m ? Number(m[1]) : NaN;
};

const findParam = (params, key) => {
    if (!Array.isArray(params)) return undefined;
    return params.find((p) => (p.apiKey || p.name) === key);
};

const lastValidValues = {};

const isMebleParameters = (params) => {
    if (!Array.isArray(params)) return false;
    const keys = params.map((p) => (p.apiKey || p.name));
    return keys.includes("Ławka_ilość") && keys.includes("Fotel_ilość");
};

const computeMeblePrice = (params) => {
    if (!Array.isArray(params)) return null;

    const pW = findParam(params, "Szerokość_stołu");
    const pH = findParam(params, "Wysokość_stołu");
    const pLen = findParam(params, "Długość_stołu");
    const pBench = findParam(params, "Ławka_ilość");
    const pChair = findParam(params, "Fotel_ilość");

    if (!pW || !pH || !pLen || !pBench || !pChair) return null;

    const W = numFromString(pW.value);
    const H = numFromString(pH.value);
    const L = numFromString(pLen.value);
    const ben = numFromString(pBench.value) || 0;
    const chr = numFromString(pChair.value) || 0;

    if (isNaN(W) || isNaN(H) || isNaN(L)) return null;

    const PRICE_STOL = 1000;
    const PRICE_LAWKA = 600;
    const PRICE_FOTEL = 300;
    const PRICE_DOPLATA = 150;

    const cenaBazowa = PRICE_STOL + ben * PRICE_LAWKA + chr * PRICE_FOTEL;
    const doplata =
        (W !== 1000 || H !== 800 || L !== 2500) ? PRICE_DOPLATA : 0;

    return cenaBazowa + doplata;
};

const validateMeble = (params) => {
    const out = params.map((p) => ({ ...p }));
    const errs = [];

    const MIN_LEN = 2000,
        MAX_LEN = 4000;
    const MIN_H = 700,
        MAX_H = 900;
    const DEF_LEN = 2500,
        DEF_H = 800,
        DEF_W = 1000;

    const pL = findParam(out, "Długość_stołu");
    const pH = findParam(out, "Wysokość_stołu");
    const pW = findParam(out, "Szerokość_stołu");
    const pBench = findParam(out, "Ławka_ilość");
    const pChair = findParam(out, "Fotel_ilość");

    if (pL) {
        const v = numFromString(pL.value);
        const key = pL.apiKey || pL.name;
        if (isNaN(v) || v < MIN_LEN || v > MAX_LEN) {
            const fallback =
                lastValidValues[key] ||
                (pL.units ? `${DEF_LEN} ${pL.units}` : String(DEF_LEN));
            pL.value = fallback;
            errs.push(
                `Długość stołu musi być w zakresie ${MIN_LEN}–${MAX_LEN} mm.`
            );
        } else {
            const withUnits = pL.units ? `${v} ${pL.units}` : String(v);
            lastValidValues[key] = withUnits;
        }
    }

    if (pH) {
        const v = numFromString(pH.value);
        const key = pH.apiKey || pH.name;
        if (isNaN(v) || v < MIN_H || v > MAX_H) {
            const fallback =
                lastValidValues[key] ||
                (pH.units ? `${DEF_H} ${pH.units}` : String(DEF_H));
            pH.value = fallback;
            errs.push(
                `Wysokość stołu musi być w zakresie ${MIN_H}–${MAX_H} mm.`
            );
        } else {
            const withUnits = pH.units ? `${v} ${pH.units}` : String(v);
            lastValidValues[key] = withUnits;
        }
    }

    if (pW) {
        const allowed = [875, 1000, 1125, 1250];
        const v = numFromString(pW.value);
        const key = pW.apiKey || pW.name;
        if (!allowed.includes(v)) {
            const fallback =
                lastValidValues[key] ||
                (pW.units ? `${DEF_W} ${pW.units}` : String(DEF_W));
            pW.value = fallback;
            errs.push(
                "Szerokość stołu może być tylko: 875, 1000, 1125 lub 1250 mm."
            );
        } else {
            const withUnits = pW.units ? `${v} ${pW.units}` : String(v);
            lastValidValues[key] = withUnits;
        }
    }

    if (pBench && pChair) {
        let L = numFromString(pBench.value);
        let K = numFromString(pChair.value);

        if (isNaN(L)) L = 0;
        if (isNaN(K)) K = 0;

        if (L < 0) L = 0;
        if (L > 2) L = 2;
        if (K < 0) K = 0;
        if (K > 6) K = 6;

        let maxBench;
        if (K <= 2) maxBench = 2;
        else if (K <= 4) maxBench = 1;
        else maxBench = 0;

        if (L > maxBench) L = maxBench;

        const uB = pBench.units ? " " + pBench.units : "";
        pBench.allowedValues = [];
        for (let i = 0; i <= maxBench; i++) {
            pBench.allowedValues.push(String(i) + uB);
        }
        pBench.value = String(L) + uB;

        const maxK = 6 - 2 * L;
        if (K > maxK) K = maxK;

        const uC = pChair.units ? " " + pChair.units : "";
        pChair.allowedValues = [];
        for (let i = 0; i <= maxK; i++) {
            pChair.allowedValues.push(String(i) + uC);
        }
        pChair.value = String(K) + uC;
    }

    const price = computeMeblePrice(out);
    if (price != null) {
        const pCena = findParam(out, "Cena");
        if (pCena) {
            pCena.value = String(price);
            pCena.readonly = true;
        }

        const pCenaDisp = findParam(out, "Cena_wyświetlana");
        if (pCenaDisp) {
            pCenaDisp.value = `${price} zł`;
            pCenaDisp.readonly = true;
        }
    }

    if (errs.length === 0) {
        return { corrected: out, error: false, msg: "" };
    }

    return {
        corrected: out,
        error: true,
        msg:
            errs.join("\n") +
            "\n\nPrzywrócono ostatnie poprawne wartości.\nModel nie został przeliczony."
    };
};

// ======================================================
// GŁÓWNA FUNKCJA — UPDATE MODEL + WALIDACJA MEBLI
// ======================================================

export const updateModelWithParameters =
    (projectId, data) => async (dispatch, getState) => {
        dispatch(addLog("updateModelWithParameters invoked"));

        const project = getProject(projectId, getState());

        const label =
            project && project.label ? String(project.label).trim() : "";

        const isMebleProject = label.startsWith("Zestaw mebli ogrodowych");
        const looksLikeMeble = isMebleParameters(data);

        let paramsToSend = data;

        if (isMebleProject && looksLikeMeble) {
            const result = validateMeble(data);
            paramsToSend = result.corrected;

            dispatch(updateParameters(projectId, paramsToSend));

            if (result.error) {
                dispatch(showModalProgress(false));
                dispatch(
                    setErrorData({
                        message: result.msg
                    })
                );
                dispatch(showUpdateFailed(true));
                return;
            }
        }

        const formatted = formatParameters(paramsToSend);
        const jobManager = Jobs();

        dispatch(showModalProgress(true));

        try {
            await jobManager.doUpdateJob(
                projectId,
                formatted,

                () => {
                    dispatch(
                        addLog(
                            "JobManager: HubConnection started for project: " +
                            projectId
                        )
                    );
                    dispatch(setErrorData(null));
                },

                (updatedState, stats, reportUrl) => {
                    dispatch(addLog("JobManager: Received onComplete"));
                    dispatch(setStats(stats));
                    dispatch(setReportUrl(reportUrl));

                    const { parameters, ...rest } = updatedState;
                    const adapted = adaptParameters(parameters);
                    dispatch(updateParameters(projectId, adapted));
                    dispatch(updateProject(projectId, rest));
                },

                (errorData) => {
                    dispatch(
                        addLog(
                            "JobManager: Received onError with jobId: " +
                            errorData.jobId
                        )
                    );
                    dispatch(showModalProgress(false));
                    dispatch(setErrorData(errorData));
                    dispatch(showUpdateFailed(true));
                }
            );
        } catch (error) {
            dispatch(addError("JobManager error: " + error));
        }
    };

// ======================================================
// PORÓWNANIA PARAMETRÓW
// ======================================================

const stripUnits = (p) => {
    if (!p.units) return p.value;
    const u = p.units.toString();
    if (p.value.endsWith(u)) {
        return p.value.slice(0, -u.length).trim();
    }
    return p.value.trim();
};

export const compareParameters = (p1, p2) => {
    if (!p1 || !p2) return false;
    if (p1.value === p2.value) return true;
    if (p1.units !== p2.units) return false;
    return stripUnits(p1) === stripUnits(p2);
};

export const parametersEqual = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;

    return a.every((pa) =>
        b.some((pb) => compareParameters(pa, pb))
    );
};